<template>
    <div class="profile-name-editor" :class="{ 'is-editing': isEditing }">
        <template v-if="!isEditing">
            <span class="body-small text-ink" :data-testid="`${testIdPrefix}-preview`">{{ userName }}</span>
            <BaseButton
                ref="editButton"
                theme="tertiary"
                size="sm"
                icon-only
                :aria-label="t('profile.editableField.name.editA11yLabel')"
                :data-testid="`${testIdPrefix}-edit`"
                @click="startEditing">
                <i class="bi bi-pencil" aria-hidden="true" />
            </BaseButton>
        </template>
        <template v-else-if="!isSaving">
            <FormField :label="t('profile.editableField.name.firstNameLabel')" required :error="firstNameError" class="name-field">
                <BaseInput
                    ref="firstNameInput"
                    v-model="editFirstName"
                    size="compact"
                    aria-required="true"
                    :data-testid="`${testIdPrefix}-first-name-input`"
                    autocomplete="given-name"
                    @keydown.enter="submitOnEnter"
                    @keydown.esc="cancelOnEscape" />
            </FormField>
            <FormField :label="t('profile.editableField.name.lastNameLabel')" :error="lastNameError" class="name-field">
                <BaseInput
                    v-model="editLastName"
                    size="compact"
                    :data-testid="`${testIdPrefix}-last-name-input`"
                    autocomplete="family-name"
                    @keydown.enter="submitOnEnter"
                    @keydown.esc="cancelOnEscape" />
            </FormField>
            <div class="name-actions">
                <BaseButton
                    theme="secondary"
                    size="sm"
                    icon-only
                    :aria-label="t('profile.editableField.name.saveA11yLabel')"
                    :data-testid="`${testIdPrefix}-submit`"
                    @click="submit">
                    <i class="bi bi-check text-lg/none" aria-hidden="true" />
                </BaseButton>
                <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('profile.editableField.name.cancelA11yLabel')" @click="cancel">
                    <i class="bi bi-x text-lg/none" aria-hidden="true" />
                </BaseButton>
            </div>
            <p v-if="saveError" role="alert" class="save-error">{{ saveError }}</p>
        </template>
        <LoadingSpinner v-else class="size-6 shrink-0" />
    </div>
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import { router } from "@inertiajs/vue3";
import { unrefElement } from "@vueuse/core";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { UserProfileEndpoints } from "~vue/UserProfile/types";
import { isImeComposing } from "~vue/utils/keyboard";
import { nextTick, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const { userName, firstName, lastName, endpoints } = defineProps<{
    userName: string;
    firstName: string;
    lastName: string;
    endpoints: UserProfileEndpoints;
}>();

const emit = defineEmits<{
    "update:editing": [editing: boolean];
}>();

const { t } = useI18n();

const testIdPrefix = "profile-name";

const isEditing = ref(false);
watch(isEditing, (editing) => emit("update:editing", editing));
const isSaving = ref(false);
const editFirstName = ref("");
const editLastName = ref("");
const firstNameError = ref("");
const lastNameError = ref("");
const saveError = ref("");

const editButton = useTemplateRef("editButton");
// BaseInput's ref points at its frame, so it exposes focus() for the inner input.
const firstNameInput = useTemplateRef<InstanceType<typeof BaseInput>>("firstNameInput");

const focusFirstInput = () => firstNameInput.value?.focus({ preventScroll: true });
const restoreFocus = () => unrefElement(editButton)?.focus({ preventScroll: true });

// Typing clears the rejection shown for a blank first name.
watch(editFirstName, () => (firstNameError.value = ""));
watch(editLastName, () => (lastNameError.value = ""));

const clearErrors = () => {
    firstNameError.value = "";
    lastNameError.value = "";
    saveError.value = "";
};

const startEditing = () => {
    editFirstName.value = firstName;
    editLastName.value = lastName;
    clearErrors();
    isEditing.value = true;
    nextTick(focusFirstInput);
};

const cancel = () => {
    clearErrors();
    isEditing.value = false;
    nextTick(restoreFocus);
};

// IME users press Enter to confirm characters and Escape to cancel a conversion,
// so neither key may act while a composition owns it.
const submitOnEnter = (event: KeyboardEvent) => {
    if (!isImeComposing(event)) {
        submit();
    }
};
const cancelOnEscape = (event: KeyboardEvent) => {
    if (!isImeComposing(event)) {
        cancel();
    }
};

const asError = (value: unknown): string => (Array.isArray(value) ? String(value[0] ?? "") : String(value ?? ""));

// Set by a failed save so onFinish can refocus once the inputs remount.
let refocusAfterSave = false;

const submit = () => {
    const newFirstName = editFirstName.value.trim();
    const newLastName = editLastName.value.trim();
    clearErrors();
    // Nothing changed: close without posting (matches the replaced editor).
    if (newFirstName === firstName && newLastName === lastName) {
        cancel();
        return;
    }
    if (!newFirstName) {
        firstNameError.value = t("profile.editableField.name.firstNameRequired");
        focusFirstInput();
        return;
    }

    router.post(
        endpoints.profile,
        { detail: "name", first_name: newFirstName, last_name: newLastName },
        {
            headers: {
                "X-CSRFToken": getCookie("csrftoken") ?? "",
                "X-Inertia-Next": endpoints.current_page,
            },
            preserveScroll: true,
            only: ["user_name", "first_name", "last_name", "errors"],
            onStart: () => (isSaving.value = true),
            onSuccess: () => {
                isEditing.value = false;
                nextTick(restoreFocus);
            },
            // A failed save keeps the draft and reports the problem. The inputs
            // stay unmounted behind the spinner until onFinish clears isSaving,
            // so the refocus has to wait for that.
            onError: (errors) => {
                firstNameError.value = asError(errors.first_name);
                lastNameError.value = asError(errors.last_name);
                saveError.value = firstNameError.value || lastNameError.value ? "" : t("profile.editableField.name.saveFailed");
                refocusAfterSave = true;
            },
            onFinish: () => {
                isSaving.value = false;
                if (refocusAfterSave) {
                    refocusAfterSave = false;
                    nextTick(focusFirstInput);
                }
            },
        },
    );
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .profile-name-editor {
        @apply inline-flex min-w-0 max-w-full flex-wrap items-end gap-2;
    }

    /* While editing the editor owns its row so the fields can take the full
       container width. */
    .profile-name-editor.is-editing {
        width: 100%;
    }

    /* Each field grows to fill the row; on narrow viewports they wrap to
       full-width rows and the actions sit on the last row, right-aligned. */
    .profile-name-editor .name-field {
        flex: 1 1 14rem;
        min-width: 0;
    }

    /* Compact inputs are a fixed 16rem; let them fill their field instead. */
    .profile-name-editor .name-field :deep(.base-input) {
        width: 100%;
    }

    .profile-name-editor .name-actions {
        @apply flex w-full items-center justify-end gap-2;
    }

    .profile-name-editor .save-error {
        @apply w-full body-small text-status-error;
    }

    @media (min-width: 40rem) {
        /* Fields and buttons share one row; the button row matches the input
           height so the buttons align with the inputs under the labels. */
        .profile-name-editor .name-actions {
            @apply w-auto;
            height: 2.375rem;
        }
    }
}
</style>
